import React from "react";

const safeQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : 0;
};

const safeAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

export default function AdminSales({
  filteredSalesOrders,
  salesDatePreset,
  setSalesDatePreset,
  salesStartDate,
  setSalesStartDate,
  salesEndDate,
  setSalesEndDate,
  salesSource,
  setSalesSource,
  salesPayment,
  setSalesPayment,
  salesStatus,
  setSalesStatus,
  orderStatuses,
  salesTotal,
  salesPaid,
  salesPending,
  salesOrderCount,
  salesItemCount,
  cancelledSales,
  sourceBreakdown,
  paymentBreakdown,
  onlineSales,
  manualSales,
  productSales,
}) {
  return (
    <div className="mt-6 space-y-6" data-testid="admin-sales">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <div className="overline">Sales</div>
              <h2 className="display-h3 mt-2">Sales Details</h2>
              <p className="text-sm text-stone-600 mt-1">
                Track revenue, payments, order sources and product sales in one place.
              </p>
            </div>
            <button
              type="button"
              className="btn-outline-pmt"
              onClick={() => {
                const rows = [
                  ["Order Number","Date","Customer","Mobile","Address","City","District","State","PIN Code","Source","Products","Quantity","Total","Payment Method","Payment Status","Order Status"],
                  ...filteredSalesOrders.map((o) => [
                    o.order_number,
                    o.created_at ? new Date(o.created_at).toLocaleString("en-IN") : "",
                    o.full_name || "",
                    o.mobile || "",
                    o.address || "",
                    o.city || "",
                    o.district || "",
                    o.state || "",
                    o.pin_code || o.pincode || "",
                    o.source || "online",
                    (o.items || []).map((item) => `${item.product_name} x ${safeQuantity(item.quantity)}`).join(" | "),
                    (o.items || []).reduce((s, item) => s + safeQuantity(item.quantity), 0),
                    safeAmount(o.total_amount).toFixed(2),
                    o.payment_method || "",
                    o.payment_status || "pending",
                    o.status || "new",
                  ]),
                ];
                const csvText = rows
                  .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
                  .join("\n");
                const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `printmytees-sales-${new Date().toISOString().slice(0,10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export Sales CSV
            </button>
          </div>

          {/* FILTERS */}
          <div className="card-pmt p-5">
            <div className="overline">Sales Filters</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <div className="label-pmt">Date Range</div>
                <select
                  className="input-pmt"
                  value={salesDatePreset}
                  onChange={(e) => setSalesDatePreset(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              <div>
                <div className="label-pmt">Order Source</div>
                <select
                  className="input-pmt"
                  value={salesSource}
                  onChange={(e) => setSalesSource(e.target.value)}
                >
                  <option value="all">All Sources</option>
                  <option value="online">Online</option>
                  <option value="social_media">Social Media</option>
                  <option value="phone">Phone</option>
                  <option value="walk-in">Walk-in</option>
                </select>
              </div>

              <div>
                <div className="label-pmt">Payment Status</div>
                <select
                  className="input-pmt"
                  value={salesPayment}
                  onChange={(e) => setSalesPayment(e.target.value)}
                >
                  <option value="all">All Payments</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partially Paid</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div>
                <div className="label-pmt">Order Status</div>
                <select
                  className="input-pmt"
                  value={salesStatus}
                  onChange={(e) => setSalesStatus(e.target.value)}
                >
                  <option value="all">All Active Orders</option>
                  {orderStatuses.filter((s) => s !== "cancelled").map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  className="btn-outline-pmt w-full"
                  onClick={() => {
                    setSalesDatePreset("all");
                    setSalesStartDate("");
                    setSalesEndDate("");
                    setSalesSource("all");
                    setSalesPayment("all");
                    setSalesStatus("all");
                  }}
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {salesDatePreset === "custom" && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <div>
                  <div className="label-pmt">Start Date</div>
                  <input
                    type="date"
                    className="input-pmt"
                    value={salesStartDate}
                    onChange={(e) => setSalesStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <div className="label-pmt">End Date</div>
                  <input
                    type="date"
                    className="input-pmt"
                    value={salesEndDate}
                    onChange={(e) => setSalesEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Gross Sales</div>
              <div className="text-2xl font-bold mt-2">₹{salesTotal.toLocaleString("en-IN")}</div>
              <div className="text-xs text-stone-500 mt-1">{salesOrderCount} orders</div>
            </div>
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Collected Amount</div>
              <div className="text-2xl font-bold mt-2">₹{salesPaid.toLocaleString("en-IN")}</div>
              <div className="text-xs text-stone-500 mt-1">{salesItemCount} items sold</div>
            </div>
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Pending / Partial Order Value</div>
              <div className="text-2xl font-bold mt-2">₹{salesPending.toLocaleString("en-IN")}</div>
            </div>
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Cancelled Amount</div>
              <div className="text-2xl font-bold mt-2">₹{cancelledSales.toLocaleString("en-IN")}</div>
              <div className="text-xs text-stone-500 mt-1">Excluded from sales totals</div>
            </div>
          </div>

          {/* SOURCE + PAYMENT */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-pmt p-5">
              <div className="overline">Sales by Source</div>
              <div className="mt-4 space-y-3">
                {sourceBreakdown.map((row) => (
                  <div key={row.source} className="flex items-center justify-between border-b border-[#E6DFD5] pb-2">
                    <span className="text-sm uppercase">{row.source}</span>
                    <span className="font-bold">₹{row.amount.toLocaleString("en-IN")}</span>
                  </div>
                ))}
                <div className="pt-1 flex items-center justify-between font-bold">
                  <span>Total</span>
                  <span>₹{salesTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>

            <div className="card-pmt p-5">
              <div className="overline">Payment Breakdown</div>
              <div className="mt-4 space-y-3">
                {paymentBreakdown.map((row) => (
                  <div key={row.method} className="flex items-center justify-between border-b border-[#E6DFD5] pb-2">
                    <span className="text-sm uppercase">{row.method.replace(/_/g, " ")}</span>
                    <span className="font-bold">₹{row.amount.toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ONLINE / MANUAL */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Online Sales</div>
              <div className="text-2xl font-bold mt-2">₹{onlineSales.toLocaleString("en-IN")}</div>
            </div>
            <div className="card-pmt p-5">
              <div className="text-xs text-stone-500 uppercase">Manual Sales</div>
              <div className="text-2xl font-bold mt-2">₹{manualSales.toLocaleString("en-IN")}</div>
            </div>
          </div>

          {/* PRODUCT SALES */}
          <div className="card-pmt p-5">
            <div className="overline">Product Sales</div>
            <div className="mt-4 overflow-auto border border-[#E6DFD5]">
              <table className="min-w-[700px] w-full text-sm">
                <thead className="text-xs uppercase text-stone-500 bg-[#F3EDE2]">
                  <tr>
                    <th className="text-left py-3 px-3">Product</th>
                    <th className="text-left px-3">Quantity Sold</th>
                    <th className="text-left px-3">Sales Amount</th>
                    <th className="text-left px-3">% of Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {productSales.map((item) => (
                    <tr key={item.name} className="border-t border-[#E6DFD5]">
                      <td className="py-3 px-3 font-semibold">{item.name}</td>
                      <td className="px-3">{item.quantity}</td>
                      <td className="px-3 font-bold">₹{item.amount.toLocaleString("en-IN")}</td>
                      <td className="px-3">{item.percentage.toFixed(1)}%</td>
                    </tr>
                  ))}
                  {productSales.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-10 text-center text-stone-500">
                        No product sales for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* SALES RECORDS */}
          <div className="card-pmt p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="overline">Sales Records</div>
                <div className="text-sm text-stone-600 mt-1">
                  {filteredSalesOrders.length} order{filteredSalesOrders.length === 1 ? "" : "s"} shown
                </div>
              </div>
            </div>

            <div className="mt-5 overflow-auto border border-[#E6DFD5]">
              <table className="min-w-[1250px] w-full text-sm">
                <thead className="text-xs uppercase text-stone-500 bg-[#F3EDE2]">
                  <tr>
                    <th className="text-left py-3 px-3">Order #</th>
                    <th className="text-left px-3">Date</th>
                    <th className="text-left px-3">Customer</th>
                    <th className="text-left px-3">Products</th>
                    <th className="text-left px-3">Qty</th>
                    <th className="text-left px-3">Source</th>
                    <th className="text-left px-3">Payment</th>
                    <th className="text-left px-3">Status</th>
                    <th className="text-left px-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSalesOrders.map((o) => (
                    <tr key={o.order_number} className="border-t border-[#E6DFD5] align-top">
                      <td className="py-3 px-3 font-mono font-bold">{o.order_number}</td>
                      <td className="px-3 whitespace-nowrap">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td className="px-3">
                        <div className="font-semibold">{o.full_name || "—"}</div>
                        <div className="text-xs text-stone-500">{o.mobile || ""}</div>
                      </td>
                      <td className="px-3">
                        {(o.items || []).map((item, i) => (
                          <div key={i}>
                            {item.product_name} × {safeQuantity(item.quantity)}
                            {item.size ? ` · ${item.size}` : ""}
                            {item.color ? ` · ${item.color}` : ""}{item.quality ? ` · ${item.quality}` : ""}
                          </div>
                        ))}
                      </td>
                      <td className="px-3">
                        {(o.items || []).reduce((s, item) => s + safeQuantity(item.quantity), 0)}
                      </td>
                      <td className="px-3 uppercase text-xs font-semibold">{o.source || "online"}</td>
                      <td className="px-3">
                        <div className="uppercase text-xs font-semibold">{o.payment_status || "pending"}</div>
                        <div className="text-xs text-stone-500">{o.payment_method || "—"}</div>
                      </td>
                      <td className="px-3 uppercase text-xs font-semibold">{o.status || "new"}</td>
                      <td className="px-3 font-bold whitespace-nowrap">₹{safeAmount(o.total_amount).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                  {filteredSalesOrders.length === 0 && (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-stone-500">
                        No sales recorded for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
  );
}
